import { Timestamp } from 'firebase/firestore';

// User types
export type UserRole = 'USER' | 'ADMIN';

export interface User {
  id: string;
  email: string;
  displayName: string | null;
  photoURL: string | null;
  authProvider: 'email' | 'twitter' | 'google';
  role: UserRole;
  xUsername?: string; // X (Twitter) username for affiliate payouts
  xrplWallet?: string; // XRPL wallet address for receiving referral payouts
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// Affiliate types
export interface AffiliateCode {
  id: string; // Document ID (the actual code)
  code: string; // The affiliate code (e.g., "username_GRID_AGENT")
  userId: string; // User who created the code
  userEmail: string;
  xUsername: string; // X username of the affiliate
  xrplWallet: string; // XRPL wallet for payouts
  totalReferrals: number; // Count of successful referrals
  totalEarnings: number; // Earnings assigned by an admin, in USD
  totalPaid: number; // Sum of payouts recorded against this affiliate, in USD
  clickupLeadId?: string; // ClickUp CRM task ID
  acceptedTermsAt?: Timestamp; // When the affiliate accepted the Agreement
  termsVersion?: string; // Version of the Agreement they accepted
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// Referral commission tiers — percentage of collected project revenue.
export type ReferralType =
  | 'WARM_INTRO'
  | 'QUALIFIED'
  | 'CLOSING_PARTNER'
  | 'ADDITIONAL_SERVICE'
  | 'RECURRING';

// Product/service line a referral or lead relates to — lets the dashboard show
// where a client sits on the Website → AI Automation → Bridging ladder.
export type ServiceLine = 'WEBSITE' | 'AI_AUTOMATION' | 'BRIDGING' | 'OTHER';

export interface Referral {
  id: string;
  affiliateCode: string; // The code that was used
  affiliateUserId: string; // User who owns the code
  referredUserId: string; // User who was referred ('manual-entry' for admin-added)
  referredEmail: string; // Referred client (name, email, or company)
  referralType?: ReferralType; // Commission tier
  serviceLine?: ServiceLine; // Product line this deal was (Website, AI Automation, Bridging, Other)
  projectName?: string; // What the project was
  projectValue?: number; // Collected project revenue (USD)
  commissionRate?: number; // Commission percentage applied
  earningsAmount?: number; // Commission earned (USD) — derived from projectValue * commissionRate
  manual?: boolean; // True if added by an admin rather than a signup
  isBonus?: boolean; // True for bonus/adjustment entries (not counted as a referral)
  bonusLabel?: string; // Label for a bonus entry (e.g. "Large Deal Bonus")
  createdAt: Timestamp;
}

// A recorded manual payment to an affiliate (e.g. an XRPL transfer you sent by hand)
export interface Payout {
  id: string;
  affiliateCode: string; // The affiliate code / document ID paid
  affiliateUserId: string; // User who owns the code
  amount: number; // USD paid in this payout
  txHash?: string; // Optional XRPL transaction hash for reconciliation
  note?: string; // Optional admin note
  paidAt: Timestamp; // When the payment was actually sent
  recordedBy: string; // Admin UID who logged the payout
  createdAt: Timestamp;
}

// CRM types — native lead/deal pipeline (replaces the ClickUp CRM embed)
export type LeadStage =
  | 'NEW'
  | 'CONTACTED'
  | 'QUALIFIED'
  | 'PROPOSAL'
  | 'WON'
  | 'LOST';

export interface CrmLead {
  id: string;
  name: string; // Contact / person name
  company?: string; // Company or organization
  email?: string;
  phone?: string;
  source?: string; // Where the lead came from (referral, web, X, etc.)
  affiliateCode?: string; // Optional link to the affiliate who brought the lead
  stage: LeadStage; // Pipeline stage
  value?: number; // Estimated / actual deal value (USD)
  serviceLine?: ServiceLine; // Product line this lead is for (Website, AI Automation, Bridging, Other)
  notes?: string; // Freeform notes
  consent?: boolean; // Prospect agreed to be contacted (self-submitted referral)
  consentAt?: Timestamp; // When consent was given
  submittedVia?: string; // How the lead arrived (e.g. 'referral-link')
  createdBy: string; // Admin UID who created the lead
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// Notes — native admin notes/docs (replaces the ClickUp Docs embed)
export interface AdminNote {
  id: string;
  title: string;
  body: string;
  tags?: string[];
  pinned?: boolean;
  createdBy: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// Calendar — native admin calendar (replaces the ClickUp Calendar embed)
export type CalendarEventType = 'MEETING' | 'DEADLINE' | 'FOLLOW_UP' | 'REMINDER' | 'OTHER';

export interface CalendarEvent {
  id: string;
  title: string;
  date: string; // ISO day, 'yyyy-MM-dd' (stored as string to avoid timezone drift)
  time?: string; // 'HH:mm' 24h, optional
  type: CalendarEventType;
  notes?: string;
  createdBy: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// Sprint — native admin task board (replaces the ClickUp Board embed)
export type SprintStatus = 'BACKLOG' | 'TODO' | 'IN_PROGRESS' | 'DONE';
export type SprintPriority = 'LOW' | 'MEDIUM' | 'HIGH';

export interface SprintTask {
  id: string;
  title: string;
  description?: string;
  status: SprintStatus;
  priority: SprintPriority;
  assignee?: string;
  createdBy: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// Ticket types
export type TicketStatus = 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED';
export type TicketType = 'ISSUE' | 'SERVICE_REQUEST';
export type TicketPriority = 'LOW' | 'MEDIUM' | 'HIGH';
export type TicketChannel = 'WEB' | 'TELEGRAM' | 'DISCORD';

export interface Attachment {
  name: string;
  url: string;
  size: number;
  type: string;
  uploadedAt: Timestamp | Date;
}

export interface Ticket {
  id: string;
  title: string;
  description: string;
  status: TicketStatus;
  type: TicketType;
  priority: TicketPriority;
  channel: TicketChannel;
  ownerId: string;
  ownerEmail: string;
  assignedToUserId: string | null;
  affiliateCode?: string | null; // Grid affiliate referral code
  relatedUrl?: string;
  externalId?: string; // For Telegram/Discord message IDs
  clickupTaskId?: string; // ClickUp sprint task ID
  attachments?: Attachment[];
  createdAt: Timestamp;
  updatedAt: Timestamp;
  resolvedAt?: Timestamp;
}

// Message types
export interface TicketMessage {
  id: string;
  ticketId: string;
  authorId: string;
  authorEmail: string;
  authorName?: string;
  authorRole: UserRole;
  content: string;
  isInternal: boolean; // Internal notes only visible to admins
  attachments?: Attachment[];
  createdAt: Timestamp;
}

// Helper function to convert Firestore Timestamp to Date
export function timestampToDate(timestamp: Timestamp | undefined): Date | undefined {
  return timestamp ? timestamp.toDate() : undefined;
}

// ClickUp integration settings (stored in Firestore settings/clickup).
// The admin CRM/Calendar/Notes/Sprint tools are now native, so the old embed
// URLs are gone; these remaining IDs still drive ticket → ClickUp task sync.
export interface ClickUpSettings {
  sprintListId: string;     // ClickUp List ID for new ticket tasks
  crmListId: string;        // ClickUp List ID for new affiliate/lead tasks
  clickupStatusMap?: Record<string, string>; // ClickUp status name → GridSupport TicketStatus
}

